import { useEffect, useMemo, useRef } from "react";
import { useLazyQuery } from "@apollo/client/react";
import { MapAddressDetailsDocument, MapInfoboxIssuesDocument } from "#app/lib/data";

import type { MapPin } from "#app/components/map/map-types";
import type { MapIssue } from "#app/components/map/map-issue";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AddressDetails = Omittable<{
  addressId: string;
  addressLine1?: string | null;
  wholeAddress?: string | null;
  city?: string | null;
  stateProvince?: string | null;
  postalCode?: string | null;
  propertyDetails?: {
    squareFootage?: number | null;
    yearBuilt?: number | null;
    ownerNames?: string[] | null;
    marketValue?: number | null;
  } | null;
}>;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useMapInfoboxData(pin: MapPin | null) {
  const [fetchIssues, { data: issuesData, loading: issuesLoading }] =
    useLazyQuery(MapInfoboxIssuesDocument, {
      fetchPolicy: "cache-and-network",
    });

  const [fetchAddress, { data: addressData, loading: addressLoading }] =
    useLazyQuery(MapAddressDetailsDocument, {
      fetchPolicy: "cache-and-network",
    });

  useEffect(() => {
    if (!pin) return;

    if (pin.issueIds.length > 0) {
      fetchIssues({ variables: { issueIds: pin.issueIds } });
    }
    if (pin.addressId) {
      fetchAddress({ variables: { addressId: pin.addressId } });
    }
  }, [pin, fetchIssues, fetchAddress]);

  const issuesDataRef = useRef(issuesData);
  const issuesRef = useRef<MapIssue[]>([]);
  const nextIssues: MapIssue[] = useMemo(
    () =>
      issuesData?.issues?.edges?.map(
        (e: { node: MapIssue }) => e.node,
      ) ?? [],
    [issuesData],
  );

  // Update issues when Apollo data reference changes.
  if (issuesDataRef.current !== issuesData) {
    issuesDataRef.current = issuesData;
    issuesRef.current = nextIssues;
  }
  const issues = issuesRef.current;

  const addressDetails: AddressDetails | null =
    addressData?.address ?? null;

  return {
    issues,
    addressDetails,
    loading: issuesLoading || addressLoading,
  };
}
